import { X, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import {
  useCabinetRootMembers,
  useUpsertCabinetMember,
  useRemoveCabinetMember,
} from '../../hooks/queries/useCabinetLibrary';
import type { CabinetFolder } from '../../services/cabinet/cabinetService';

interface Props {
  rootFolder: CabinetFolder;
  onClose: () => void;
}

export function CabinetMembersModal({ rootFolder, onClose }: Props) {
  const { data: collaborators = [] } = useCollaborators();
  const { data: members = [] } = useCabinetRootMembers(rootFolder.id);
  const upsert = useUpsertCabinetMember(rootFolder.id);
  const remove = useRemoveCabinetMember(rootFolder.id);

  const byId = new Map(members.map((m) => [m.collaborator_id, m]));

  // Admins are excluded from the list — ils ont déjà accès via bypass DB.
  const eligible = collaborators.filter((c) => c.is_active && c.role !== 'admin');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <div>
            <h2 className="font-heading text-lg font-semibold">Accès à « {rootFolder.name} »</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Les administrateurs ont accès par défaut.</p>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {eligible.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">Aucun collaborateur disponible.</p>
          ) : (
            <table className="cime-table text-sm">
              <thead>
                <tr>
                  <th className="py-2">Collaborateur</th>
                  <th className="py-2 text-center w-20">Voir</th>
                  <th className="py-2 text-center w-20">Éditer</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {eligible.map((c) => {
                  const m = byId.get(c.id);
                  const canView = m?.can_view ?? false;
                  const canEdit = m?.can_edit ?? false;
                  return (
                    <tr key={c.id}>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <CollaboratorLink collaboratorId={c.id} title={c.full_name ? `Voir le profil de ${c.full_name}` : undefined}>
                            {c.avatar_url ? (
                              <img src={c.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center text-[10px] text-neutral-500">
                                {c.full_name?.[0]?.toUpperCase() ?? '?'}
                              </div>
                            )}
                          </CollaboratorLink>
                          <div>
                            <CollaboratorLink collaboratorId={c.id} title={c.full_name ? `Voir le profil de ${c.full_name}` : undefined}>
                              <div className="font-medium">{c.full_name ?? c.email}</div>
                            </CollaboratorLink>
                            <div className="text-[11px] text-neutral-500">{c.role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 text-center">
                        <input
                          type="checkbox"
                          checked={canView}
                          onChange={(e) => {
                            const v = e.target.checked;
                            upsert.mutate({
                              folderId: rootFolder.id,
                              collaboratorId: c.id,
                              canView: v,
                              canEdit: v ? canEdit : false,
                            });
                          }}
                        />
                      </td>
                      <td className="py-2 text-center">
                        <input
                          type="checkbox"
                          checked={canEdit}
                          disabled={!canView}
                          onChange={(e) => {
                            upsert.mutate({
                              folderId: rootFolder.id,
                              collaboratorId: c.id,
                              canView: true,
                              canEdit: e.target.checked,
                            });
                          }}
                        />
                      </td>
                      <td className="py-2 text-right">
                        {m && (
                          <button
                            type="button"
                            onClick={() => remove.mutate({ collaboratorId: c.id })}
                            className="p-1 text-neutral-400 hover:text-red-600"
                            title="Retirer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <footer className="flex items-center justify-end px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button onClick={onClose}>Fermer</Button>
        </footer>
      </div>
    </div>
  );
}
